// FAR-67: explain a Planning Board number that is larger than the plant's own
// warehouse rows.
//
// After FAR-65 a plant plans every item it MAKES, including demand from other
// branch warehouses (22 such rows at Faribault: 16 from Fulcrum, 6 from
// Tucson). A planner comparing the board with Faribault's own forecast rows
// would otherwise see higher numbers and no explanation.
//
// Read-only: this is wording only, derived from what the engine already
// computed.

// Warehouse and plant names, so a tooltip reads "Fulcrum (1106)" not "1106".
// Falls back to the bare code when the reference data hasn't loaded.
export function plantName(code, names) {
  const name = names?.[code]
  return name ? `${name} (${code})` : String(code)
}

const sumMonths = (byMonth) =>
  Object.values(byMonth || {}).reduce((total, cases) => total + (cases || 0), 0)

// warehouseBreakdown(row) -> [{ code, cases, isOwn }], biggest first.
// `own` is the making plant's own warehouse; the rest are what FAR-67 flags.
export function warehouseBreakdown(row, makingPlant) {
  const byWarehouse = row?.warehouseDemand
  if (!byWarehouse) return []
  return Object.entries(byWarehouse)
    .map(([code, byMonth]) => ({
      code,
      cases: sumMonths(byMonth),
      isOwn: code === makingPlant,
    }))
    .sort((a, b) => b.cases - a.cases)
}

// demandSourceTooltip(row, makingPlant, names) -> the lines a tooltip shows,
// or [] when there is nothing to explain (an item served only from its own
// warehouse, which must show no indicator at all).
export function demandSourceTooltip(row, makingPlant, names) {
  if (!row?.hasOtherWarehouseDemand && !(row?.alsoMadeAt?.length > 0)) return []

  const lines = []
  const breakdown = warehouseBreakdown(row, makingPlant)
  if (breakdown.some((w) => !w.isOwn)) {
    lines.push('This demand includes other branch warehouses:')
    for (const w of breakdown) {
      lines.push(`${plantName(w.code, names)}: ${Math.round(w.cases).toLocaleString()} cases${w.isOwn ? ' (this plant)' : ''}`)
    }
  }

  // The 18 items made at both plants: the other plant covers its own
  // warehouse, so this plant plans only part of the item's total demand.
  for (const code of row.alsoMadeAt || []) {
    lines.push(`${plantName(code, names)} also makes this item for its own warehouse, so it is not planned here.`)
  }
  return lines
}
